// Field-wide skins pool. Pure, deterministic — no imports outside ./handicap and domain
// types, matching the rest of engine/*.
//
// A round_games SKINS pool (supabase/migrations/0010, engine/games/skins.ts) is per FOURSOME:
// four players, four cards, one pot among them. Sunday's outing (2026-09-15) wants the WHOLE
// FIELD in one pot instead — every player who opts in pays the same buy-in, and on each hole
// the single lowest score across EVERY card in the event wins, not just the lowest in one
// group. That needs every player's score on a hole before it can decide anything, which a
// per-foursome engine has no way to see, so this gets its own engine rather than being folded
// into engine/games/skins.ts. See supabase/migrations/0024_field_skins.sql for the tournament
// switch (`field_skins`/`field_skins_buy_in`/`field_skins_carry`) and src/sync/leaderboard.ts's
// `fieldSkinsFromPayload` for how a wire payload becomes this engine's input.
//
// TIES. Two or more players sharing the low score on a hole means nobody wins it outright.
// `carry: true` — the default a host almost always wants — rolls that hole's stake into the
// next hole that IS decided outright, the same "carryover" convention every skins game and
// engine/games/skins.ts itself already use: a double, triple, etc. skin. `carry: false` voids
// the hole instead: the stake is never paid to anyone, tallied into `unclaimedSkins` so both
// the payout math and a host reading the board can see where it went.
//
// PENDING. The pool only pays out through holes the WHOLE FIELD has finished — a hole where
// half the field has holed out and the other half is still on the back nine is not a hole
// anyone has actually settled. The sweep stops at the first hole missing any player's score,
// and every hole after it is `pending` too: skins never resolve out of order, the same reason
// leaderboard.ts's own `thru` is a count of completed holes rather than a highest-hole guess.

import { allocateStrokes } from './handicap';

export type FieldSkinsBasis = 'gross' | 'net';

export type FieldSkinsPlayer = {
  id: string;
  name: string;
  /** Null plays scratch — the same convention `allocateStrokes` and every other engine here
   *  follow for a player with no handicap index on file. */
  courseHandicap: number | null;
  /** One entry per played hole, 0-based; null/undefined for a hole not yet scored. */
  scores: Array<number | null | undefined>;
};

export type FieldSkinsInput = {
  basis: FieldSkinsBasis;
  /** A tie rolls into the next outright win when true; voids the hole when false. */
  carry: boolean;
  /** Per-player buy-in, in cents. The pot is this times the number of players entered. Not
   *  read when `perSkinCents` is set — see that field. */
  buyInCents: number;
  /** Cents. 0 (default): the buy-in pot above, split proportionally by skins won. Greater
   *  than 0: Sunday's outing wants a flat payout instead ($20 a skin) — every skin actually
   *  won pays exactly this amount and the "pot" reported is just skins won × this figure, not
   *  a number fixed in advance. A carried-but-unclaimed skin (a void, or a carry riding when
   *  the round ends) is still nobody's — it stays in `unclaimedSkins`, unpaid, the same as
   *  the proportional mode. */
  perSkinCents?: number;
  holeCount: number;
  players: FieldSkinsPlayer[];
  /** Stroke index per played hole. Required to net anything — under `basis: 'gross'` it is
   *  never read, and under `basis: 'net'` a wrong-length array is treated the same as a
   *  player with no scores at all on those holes would be: this engine does not repair or
   *  guess a caller's stroke indexes (see `fieldSkinsFromPayload` for the validation a caller
   *  is expected to do first). */
  strokeIndexes: number[];
};

export type FieldSkinsHole = {
  /** 0-based, matching every other hole index in engine/*. */
  hole: number;
  winnerId: string | null;
  /** Skins actually awarded to `winnerId` on this hole. 0 unless `winnerId` is set. */
  skins: number;
  /** This hole tied and, under `carry`, rolled its stake into the next outright win. Never
   *  true when `carry` is false — a voided tie is not a carry. */
  carried: boolean;
  /** The low score on this hole (gross or net, per `basis`), or null while pending. */
  low: number | null;
  tied: boolean;
  /** Not yet decided — this hole, or an earlier one, is missing a player's score. */
  pending: boolean;
};

export type FieldSkinsPlayerResult = { id: string; name: string; skins: number; payoutCents: number };

export type FieldSkinsResult = {
  holes: FieldSkinsHole[];
  players: FieldSkinsPlayerResult[];
  potCents: number;
  /** Skins nobody ever won: a voided tie (`carry: false`), or a carry still riding when the
   *  sweep reaches the last hole with nobody left to win it outright. A carry blocked by a
   *  PENDING hole is not counted here yet — more scores could still resolve it, so it is not
   *  lost, only undecided; re-running this function once those scores arrive is what resolves
   *  it, the same way every other figure here is a snapshot rather than incremental state. */
  unclaimedSkins: number;
};

/**
 * Sweep the field hole by hole. A hole is decided only when every player in `players` has a
 * score on it; the first missing score stops the sweep and marks that hole and everything
 * after it `pending`, never skipping ahead to a later hole that happens to be fully in.
 *
 * Payout is proportional to skins won: `pot × player's skins / total skins won`, in integer
 * cents, with the leftover cent(s) from rounding down every share going to the LAST player to
 * win a skin outright — so the field's total payout always equals the pot exactly whenever at
 * least one skin was actually won. With zero skins won (nothing decided yet, or every decided
 * hole tied and voided) every payout is 0; the pot is still reported so a host can see what is
 * riding on the pool.
 */
export function fieldSkins(input: FieldSkinsInput): FieldSkinsResult {
  const { basis, carry, buyInCents, perSkinCents = 0, holeCount, players, strokeIndexes } = input;

  const netUsable = basis === 'net' && strokeIndexes.length === holeCount;
  const strokesByPlayer = new Map(
    players.map((p) => [p.id, netUsable ? allocateStrokes(p.courseHandicap ?? 0, strokeIndexes) : ([] as number[])])
  );

  const holes: FieldSkinsHole[] = [];
  const skinsWon: Record<string, number> = Object.fromEntries(players.map((p) => [p.id, 0]));

  // The skins currently riding on the next outright win — 1 by default, bumped by a tie under
  // `carry`, reset to 1 the moment somebody actually wins a hole (or a tie voids instead).
  let stake = 1;
  let stopped = false;
  let lastWinnerId: string | null = null;
  let unclaimedSkins = 0;

  for (let h = 0; h < holeCount; h++) {
    if (stopped) {
      holes.push({ hole: h, winnerId: null, skins: 0, carried: false, low: null, tied: false, pending: true });
      continue;
    }

    const effective: Array<{ id: string; value: number }> = [];
    let allScored = players.length > 0;
    for (const p of players) {
      const gross = p.scores[h];
      if (gross === null || gross === undefined) {
        allScored = false;
        break;
      }
      const strokes = strokesByPlayer.get(p.id)?.[h] ?? 0;
      effective.push({ id: p.id, value: basis === 'net' ? gross - strokes : gross });
    }

    if (!allScored) {
      stopped = true;
      holes.push({ hole: h, winnerId: null, skins: 0, carried: false, low: null, tied: false, pending: true });
      continue;
    }

    const low = Math.min(...effective.map((e) => e.value));
    const winners = effective.filter((e) => e.value === low);
    const tied = winners.length > 1;

    if (tied) {
      if (carry) {
        stake += 1;
        holes.push({ hole: h, winnerId: null, skins: 0, carried: true, low, tied: true, pending: false });
      } else {
        // Void: this stake is gone, not rolled forward, so the next hole starts fresh at 1.
        unclaimedSkins += stake;
        stake = 1;
        holes.push({ hole: h, winnerId: null, skins: 0, carried: false, low, tied: true, pending: false });
      }
      continue;
    }

    const winnerId = winners[0].id;
    skinsWon[winnerId] = (skinsWon[winnerId] ?? 0) + stake;
    holes.push({ hole: h, winnerId, skins: stake, carried: false, low, tied: false, pending: false });
    lastWinnerId = winnerId;
    stake = 1;
  }

  // The sweep reached the true end of the round (never stopped on a pending hole) with a
  // carry still riding — the last decided hole tied and there is nowhere left for it to roll
  // to. That stake is gone for good, unlike one merely blocked by a pending hole above.
  if (!stopped && stake > 1) unclaimedSkins += stake;

  const totalSkinsWon = Object.values(skinsWon).reduce((s, v) => s + v, 0);

  // FIXED PER-SKIN PAYOUT. Every skin actually won pays `perSkinCents` flat — no pot to
  // split, so no rounding remainder to push onto a last winner either. The "pot" reported is
  // simply what got paid out: skins won × the flat rate. A skin nobody won (voided, or a
  // carry still riding at the end) contributes nothing here — it is already in
  // `unclaimedSkins` from the sweep above, and stays unpaid.
  if (perSkinCents > 0) {
    const potCents = perSkinCents * totalSkinsWon;
    const playerResults: FieldSkinsPlayerResult[] = players.map((p) => {
      const skins = skinsWon[p.id] ?? 0;
      return { id: p.id, name: p.name, skins, payoutCents: skins * perSkinCents };
    });
    return { holes, players: playerResults, potCents, unclaimedSkins };
  }

  // PROPORTIONAL POT. Unchanged from before `perSkinCents` existed.
  const potCents = buyInCents * players.length;
  const playerResults: FieldSkinsPlayerResult[] = players.map((p) => {
    const skins = skinsWon[p.id] ?? 0;
    const payoutCents = totalSkinsWon > 0 && skins > 0 ? Math.floor((potCents * skins) / totalSkinsWon) : 0;
    return { id: p.id, name: p.name, skins, payoutCents };
  });

  if (totalSkinsWon > 0 && lastWinnerId) {
    const allocated = playerResults.reduce((s, p) => s + p.payoutCents, 0);
    const remainder = potCents - allocated;
    if (remainder > 0) {
      const last = playerResults.find((p) => p.id === lastWinnerId);
      if (last) last.payoutCents += remainder;
    }
  }

  return { holes, players: playerResults, potCents, unclaimedSkins };
}
